import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Terminal } from "lucide-react";


/** Runs the REAL linters (ruff + pyflakes + eslint) server-side and shows results.
 * Ground truth against external AI-generated 'code quality reports' that hallucinate. */
export default function VerifyLintCard() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dev/lint");
      setReport(data);
      const totals = data.totals || {};
      if (totals.errors === 0 && totals.warnings === 0) {
        toast.success("Codebase is clean 🧼");
      } else {
        toast.warning(`${totals.errors} error(s), ${totals.warnings} warning(s)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lint runner unavailable");
    } finally { setBusy(false); }
  };

  const totals = report?.totals || {};
  const clean = report && totals.errors === 0 && totals.warnings === 0;

  return (
    <div className="card-sharp p-5" data-testid="verify-lint-card">
      <div className="flex items-center gap-3 mb-3">
        <ShieldCheck className="text-[var(--accent)]" size={18}/>
        <div className="flex-1">
          <h3 className="font-display text-base text-white">Verify Lint</h3>
          <p className="text-[11px] font-mono text-[var(--text-muted)]">
            Runs the real linters (ruff · pyflakes · eslint) server-side. Ground truth against external code-quality reports.
          </p>
        </div>
        <button data-testid="verify-lint-run" onClick={run} disabled={busy}
          className="btn-primary flex items-center gap-2 !py-1.5 !px-3 text-xs">
          {busy ? <RefreshCw size={12} className="animate-spin"/> : <Terminal size={12}/>}
          {busy ? "Running…" : "Verify"}
        </button>
      </div>

      {report && (
        <div className="mt-2 border-t border-white/5 pt-4" data-testid="verify-lint-report">
          <div className={`flex items-center gap-2 mb-3 ${clean ? "text-[var(--profit)]" : "text-[var(--warning)]"}`}>
            {clean ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
            <span className="font-display text-lg">
              {clean ? "Codebase is clean" : `${totals.errors} error(s), ${totals.warnings} warning(s)`}
            </span>
            {report.cached && (
              <span className="chip !text-[var(--text-muted)]">cached (&lt;60s)</span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3 text-xs font-mono">
            <ToolTile name="ruff" data={report.results.ruff} testId="lint-ruff"/>
            <ToolTile name="pyflakes" data={report.results.pyflakes} testId="lint-pyflakes"/>
            <ToolTile name="eslint" data={report.results.eslint} testId="lint-eslint"/>
          </div>

          {!clean && (
            <button data-testid="verify-lint-expand" onClick={()=>setExpanded(v => !v)}
              className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--accent)]">
              {expanded ? "▼ Hide details" : "▶ Show details"}
            </button>
          )}

          {expanded && !clean && (
            <div className="mt-3 max-h-64 overflow-auto border border-white/5 rounded p-3 space-y-1"
              data-testid="verify-lint-findings">
              {Object.entries(report.results).flatMap(([tool, r]) =>
                (r.findings || []).map((f, i) => (
                  <div key={`${tool}-${i}`} className="text-[11px] font-mono flex gap-2">
                    <XCircle size={11} className={`shrink-0 mt-0.5 ${f.severity === "error" ? "text-[var(--loss)]" : "text-[var(--warning)]"}`}/>
                    <span className="text-[var(--text-muted)]">{tool}</span>
                    <span className="text-[var(--accent)]">{f.file}:{f.line}</span>
                    <span className="text-[var(--text-muted)]">[{f.code}]</span>
                    <span className="text-[var(--text-secondary)]">{f.message}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {!report && !busy && (
        <p className="text-[11px] font-mono text-[var(--text-muted)] italic">
          Click "Verify" to run the linters now.
        </p>
      )}
    </div>
  );
}


function ToolTile({ name, data, testId }) {
  const count = data.count ?? 0;
  const color = count === 0 ? "text-[var(--profit)]" : "text-[var(--warning)]";
  return (
    <div className="border border-white/5 rounded p-2 text-center" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{name}</div>
      <div className={`font-display text-xl mt-0.5 ${color}`}>{count === 0 ? "✓" : count}</div>
      <div className="text-[9px] text-[var(--text-muted)]">
        {count === 0 ? "clean" : count === 1 ? "1 finding" : `${count} findings`}
      </div>
    </div>
  );
}
